import { createSlice } from '@reduxjs/toolkit'

import { RootState } from '../store'

import {
  confirmForgotPasswordWithCode,
  confirmSignUpWithVerificationCode,
  createUserFromToken,
  forgotPasswordWithCode,
  getTokenForAssociationCode,
  loginWithUsernamePasswordV2,
  refreshToken,
  signUpWithUsernamePasswordV2,
} from './authActions'
import { AuthenticatedTokenUser } from '../../models/User'
import { readToken, removeToken } from '../token/token'

interface LoginChallenge {
  name: string
  session: string
}

interface LoginRedirect {
  accountId: string | null
  url: string | null
}

interface AuthState {
  user: AuthenticatedTokenUser | null
  onboardingEnabled: boolean
  onboardingCode: string | null
  onboardingEmail: string | null
  onboardingUserId: string | null
  onboardingBDSource: string | null
  errors: Array<any> | null
  message: string | null
  loginRedirect: LoginRedirect
  loginChallenge?: LoginChallenge
  forgotPasswordStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  loginStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  blockedLandingPageHtml: string | null
}

const handleHtmlOrStandardLoginErrors = (state: AuthState, errorPayload: any) => {
  if (errorPayload?.contentType === 'text/html' && errorPayload?.htmlContent) {
    state.blockedLandingPageHtml = errorPayload.htmlContent
    state.loginStatus = 'failed'
    return
  }

  if (errorPayload && errorPayload.errors) {
    state.errors = errorPayload.errors
  } else {
    state.errors = [{ message: errorPayload?.message || 'Something went wrong!' }]
  }
  state.loginStatus = 'failed'
}

const getInitialUser = (): AuthenticatedTokenUser | null => {
  const token = readToken()
  if (token && !token.isAccessTokenExpired) {
    return createUserFromToken(token)
  }
  return null
}

const initialState: AuthState = {
  user: getInitialUser(),
  onboardingEnabled: true,
  onboardingCode: null,
  onboardingEmail: null,
  onboardingUserId: null,
  onboardingBDSource: null,
  errors: null,
  message: null,
  loginRedirect: {
    accountId: null,
    url: null,
  },
  forgotPasswordStatus: 'idle',
  loginStatus: 'idle',
  blockedLandingPageHtml: null,
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state, { payload }) => {
      removeToken()
      state.loginRedirect.url = `${payload.pathname ?? ''}${payload.search ?? ''}`
      state.user = null
      state.loginStatus = 'idle'
      state.loginChallenge = undefined
    },
    setLoginRedirectUrl: (state, { payload }) => {
      state.loginRedirect.url = payload
    },
    clearAuth: (state) => {
      state.user = null
      state.loginStatus = 'idle'
      state.onboardingCode = null
      state.onboardingEmail = null
      state.errors = null
      state.message = null
      state.loginChallenge = undefined
      state.loginRedirect.accountId = null
      state.blockedLandingPageHtml = null
    },
    clearAuthErrors: (state) => {
      state.errors = null
    },
    clearForgotPasswordStatus: (state) => {
      state.forgotPasswordStatus = 'idle'
    },
    setAuthErrors(state, { payload }) {
      state.errors = payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginWithUsernamePasswordV2.pending, (state) => {
      state.errors = null
      state.loginStatus = 'loading'
    })
    builder.addCase(loginWithUsernamePasswordV2.fulfilled, (state, { payload }) => {
      if ('message' in payload) {
        state.message = payload.message
      } else {
        state.user = payload
      }
      state.loginStatus = 'succeeded'
      state.onboardingCode = null
      state.onboardingEmail = null
      state.onboardingBDSource = null
      state.loginChallenge = undefined
    })
    // @ts-ignore
    builder.addCase(loginWithUsernamePasswordV2.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
      state.loginStatus = 'failed'
    })
    builder.addCase(getTokenForAssociationCode.pending, (state) => {
      state.errors = null
      state.message = null
      state.loginStatus = 'loading'
    })
    builder.addCase(getTokenForAssociationCode.fulfilled, (state, { payload }) => {
      const { user, onboardingEnabled, onboardingCode, onboardingEmail, message } = payload
      state.user = user
      state.loginStatus = 'succeeded'
      state.onboardingEnabled = onboardingEnabled ?? false
      state.onboardingCode = onboardingCode || null
      state.onboardingEmail = onboardingEmail || null
      state.message = message
      state.loginChallenge = undefined
    })
    // @ts-ignore
    builder.addCase(getTokenForAssociationCode.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
      state.loginStatus = 'failed'
    })
    builder.addCase(signUpWithUsernamePasswordV2.pending, (state) => {
      state.errors = null
      state.loginStatus = 'loading'
    })
    builder.addCase(signUpWithUsernamePasswordV2.fulfilled, (state) => {
      state.errors = null
      state.loginChallenge = { name: 'confirmationCode', session: '' }
      state.loginStatus = 'succeeded'
    })
    // @ts-ignore
    builder.addCase(signUpWithUsernamePasswordV2.rejected, (state, { payload }) => {
      const errorPayload = payload as any
      handleHtmlOrStandardLoginErrors(state as AuthState, errorPayload)
    })
    builder.addCase(confirmSignUpWithVerificationCode.pending, (state) => {
      state.errors = null
      state.message = null
      state.loginStatus = 'loading'
    })
    builder.addCase(confirmSignUpWithVerificationCode.fulfilled, (state, { payload }) => {
      const { user, onboardingEnabled, onboardingCode, onboardingEmail, onboardingUserId, message, onboardingBDSource } = payload
      state.user = user
      state.onboardingEnabled = onboardingEnabled ?? false
      state.onboardingCode = onboardingCode || null
      state.onboardingEmail = onboardingEmail || null
      state.onboardingUserId = onboardingUserId || null
      state.onboardingBDSource = onboardingBDSource || null
      state.message = message
      state.loginChallenge = undefined
      state.loginStatus = 'succeeded'
    })
    // @ts-ignore
    builder.addCase(confirmSignUpWithVerificationCode.rejected, (state, { payload }) => {
      const errorPayload = payload as any
      handleHtmlOrStandardLoginErrors(state as AuthState, errorPayload)
    })
    builder.addCase(forgotPasswordWithCode.pending, (state) => {
      state.errors = null
      state.forgotPasswordStatus = 'loading'
    })
    // @ts-ignore
    builder.addCase(forgotPasswordWithCode.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
      state.forgotPasswordStatus = 'failed'
    })
    builder.addCase(forgotPasswordWithCode.fulfilled, (state) => {
      state.forgotPasswordStatus = 'succeeded'
    })
    builder.addCase(confirmForgotPasswordWithCode.pending, (state) => {
      state.forgotPasswordStatus = 'loading'
      state.errors = null
    })
    // @ts-ignore
    builder.addCase(confirmForgotPasswordWithCode.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
      state.forgotPasswordStatus = 'failed'
    })
    builder.addCase(confirmForgotPasswordWithCode.fulfilled, (state) => {
      state.forgotPasswordStatus = 'succeeded'
    })
    builder.addCase(refreshToken.fulfilled, (state, { payload }) => {
      if ('message' in payload) {
        state.message = payload.message
      } else {
        state.user = payload
      }
      state.onboardingCode = null
      state.onboardingEmail = null
      state.loginChallenge = undefined
    })
    builder.addCase(refreshToken.pending, (state) => {
      state.errors = null
    })
    // @ts-ignore
    builder.addCase(refreshToken.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
      removeToken()
      state.user = null
      state.loginChallenge = undefined
    })
  },
})
export const { logout, setLoginRedirectUrl, clearAuth, clearAuthErrors, setAuthErrors, clearForgotPasswordStatus } = authSlice.actions

export const selectAuthenticatedUser = (state: RootState) => state.auth.user
export const selectOnboardingEnabled = (state: RootState) => state.auth.onboardingEnabled
export const selectOnboardingCode = (state: RootState) => state.auth.onboardingCode
export const selectOnboardingEmail = (state: RootState) => state.auth.onboardingEmail
export const selectOnboardingUserId = (state: RootState) => state.auth.onboardingUserId
export const selectOnboardingBDSource = (state: RootState) => state.auth.onboardingBDSource
export const selectAccountId = (state: RootState) => {
  if (!state.auth.user) {
    throw new Error('No account id available without an authenticated user.')
  }
  return state.auth.user.accountId
}
export const selectAuthErrors = (state: RootState) => state.auth.errors
export const selectAuthMessage = (state: RootState) => state.auth.message
export const selectLoginChallenge = (state: RootState) => state.auth.loginChallenge
export const selectLoginRedirect = (state: RootState) => state.auth.loginRedirect
export const selectForgotPasswordStatus = (state: RootState) => state.auth.forgotPasswordStatus
export const selectLoginStatus = (state: RootState) => state.auth.loginStatus
export const selectBlockedLandingPageHtml = (state: RootState) => state.auth.blockedLandingPageHtml

export default authSlice.reducer
